/**
 * DriverFines - List of driver's traffic fines
 */
import React, { useState, useEffect } from 'react';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import { Scale, Calendar, AlertCircle } from 'lucide-react';
import { driverMonoAPI } from '../../../api/missions.js';
import { useFormat } from '../../../i18n/format.js';

const STATUS_LABELS = {
  received: { label: 'Reçue', color: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300' },
  pending_designation: { label: 'Désignation en cours', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  designated: { label: 'Désignée', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  paid: { label: 'Payée', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  contested: { label: 'Contestée', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  cancelled: { label: 'Annulée', color: 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400' },
};

export default function DriverFines() {
  // Devise de l'organisation de l'amende - un seul outil monetaire.
  const { money } = useOrgCurrency();
  const { fmt } = useFormat();
  const [fines, setFines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadFines();
  }, []);

  const loadFines = async () => {
    try {
      setLoading(true);
      const res = await driverMonoAPI.getFines({ _limit: 50 });
      setFines(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
        <p className="text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (fines.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-8 text-center">
        <Scale className="h-12 w-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-slate-400">Aucune amende enregistrée</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {fines.map(fine => {
        const statusInfo = STATUS_LABELS[fine.status] || { label: fine.status, color: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300' };
        return (
          <div
            key={fine.id}
            className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Scale className="h-4 w-4 text-red-500 dark:text-red-400" />
                <span className="font-medium text-gray-900 dark:text-white">
                  {fine.infraction_type || fine.nature || 'Infraction'}
                </span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusInfo.color}`}>
                  {statusInfo.label}
                </span>
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">
                {fine.amount
                  ? money(fine.amount, fine)
                  : '—'}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-slate-400">
              {fine.infraction_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {fmt.date(fine.infraction_date, {
                    day: '2-digit', month: 'short', year: 'numeric'
                  })}
                </span>
              )}
              {fine.reference && (
                <span className="font-mono">Réf: {fine.reference}</span>
              )}
              {fine.location && (
                <span className="truncate">Lieu: {fine.location}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
